/**
 * StarEaterBoss.ts
 * World 5 boss — Eclipse Bastion. Celestial titan with three escalating
 * arena phases and a 12 HP pool (4 HP per phase).
 *
 * Behaviour: anchored at the centre of the arena, the Star-Eater holds
 * the player at bay through a repeating telegraph → fire → recover
 * loop. Each phase tightens the cycle and dilates the bob amplitude as
 * the titan's grip on the constellation slips. Vulnerable only during
 * the RECOVER window (same gating mechanic as PrismLeviathan, but the
 * phase escalation is what differentiates the W5 fight).
 *
 * Phase progression (drives both timing config and bob amplitude):
 *   PHASE 1: HP 12 → 9   slow telegraphs, gentle bob
 *   PHASE 2: HP 8  → 5   quicker telegraphs, wider bob
 *   PHASE 3: HP 4  → 0   rapid telegraphs, violent bob
 *
 * State machine (independent of phase):
 *   WAIT → TELL → RECOVER → WAIT (loop), → DEAD on hp <= 0.
 *
 * The constellation-trap projectile itself is deferred to the level
 * combat layer via a single-tick `fireRequested` pulse on TELL exit
 * (same pattern as SparkImp, TideMirror, KingRivet, PrismLeviathan).
 * The titan is autonomous — `nova` is accepted for the Enemy contract
 * but unused.
 *
 * Filename note: a different `StarEater` lives under
 * `src/game/entities/space-game/` for the W2 space-shmup scout drone.
 * The W5 boss class is `StarEaterBoss` to disambiguate.
 *
 * Rules (AGENTS.md):
 *   - No React / Skia / async imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 5 boss greybox stub. Wired into levels when W5 ships.
 * See ROADMAP.md Phase 4 (`StarEater.ts` row).
 *
 * @module StarEaterBoss
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import Logger from '../../utils/logger';

export type StarEaterBossState = 'WAIT' | 'TELL' | 'RECOVER' | 'DEAD';
export type StarEaterBossPhase = 1 | 2 | 3;

/**
 * Projectile-impact result. `BLOCKED` outside the RECOVER window
 * (titan absorbs the hit); `HURT` only during RECOVER.
 */
export type StarEaterBossHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Titan hitbox dimensions in logical pixels — large silhouette. */
export const STAR_EATER_BOSS_WIDTH = 80;
export const STAR_EATER_BOSS_HEIGHT = 80;
/** Greybox render colour — deep indigo (Crown of Night). */
export const STAR_EATER_BOSS_COLOR = '#1a1d4f';
/** Single-pool HP. 4 HP per phase × 3 phases = 12 total. */
export const STAR_EATER_BOSS_MAX_HP = 12;

interface PhaseConfig {
  readonly wait: number;
  readonly tell: number;
  readonly recover: number;
  readonly bobAmp: number;
}

/**
 * Per-phase timing and bob amplitude. Each higher phase tightens the
 * cycle and widens the bob — the titan grows more agitated as the
 * constellation re-forms.
 */
const PHASE_CONFIG: Record<StarEaterBossPhase, PhaseConfig> = {
  1: { wait: 4.0, tell: 1.5, recover: 1.5, bobAmp: 8 },
  2: { wait: 2.5, tell: 1.0, recover: 1.0, bobAmp: 16 },
  3: { wait: 1.5, tell: 0.7, recover: 0.7, bobAmp: 24 },
};

/** Bob period (s) — constant across phases; amplitude scales per phase. */
const BOB_PERIOD = 2.0;
const BOB_OMEGA = (2 * Math.PI) / BOB_PERIOD;

/** HP thresholds that trigger phase escalation. */
const PHASE_2_THRESHOLD = 8;
const PHASE_3_THRESHOLD = 4;

/**
 * StarEaterBoss — World 5 boss (Eclipse Bastion).
 *
 * Spawn with `new StarEaterBoss(centerX, centerY)`. The titan is
 * stationary on the X axis and bobs around `centerY`. Call
 * `update(dt, level, nova?)` each fixed tick. Read `fireRequested` for
 * the single-tick projectile pulse, `phase` for phase transitions,
 * `hp` for HUD, and `dead` to know when to remove.
 */
export class StarEaterBoss extends BaseEntity {
  public readonly entityType = 'StarEaterBoss';
  public readonly color = STAR_EATER_BOSS_COLOR;
  public readonly maxHp = STAR_EATER_BOSS_MAX_HP;
  public state: StarEaterBossState = 'WAIT';

  /**
   * True once dead. StarEaterBoss has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Current arena phase. Advances on HP threshold crossings. */
  public phase: StarEaterBossPhase = 1;
  /** Remaining HP. Decremented only by hits landed during RECOVER. */
  public hp = STAR_EATER_BOSS_MAX_HP;
  /** Always faces left — titan is not directional. */
  public facingRight = false;
  /** Single-tick pulse — true on the tick the titan exits TELL. */
  public fireRequested = false;
  private readonly _centerX: number;
  private readonly _centerY: number;
  private _waitTimer: number;
  private _tellTimer = 0;
  private _recoverTimer = 0;
  /** Phase accumulator for the vertical bob. */
  private _bobPhase = 0;

  constructor(centerX: number, centerY: number) {
    super(centerX, centerY, STAR_EATER_BOSS_WIDTH, STAR_EATER_BOSS_HEIGHT);
    this._centerX = centerX;
    this._centerY = centerY;
    // DECISION: prime the WAIT timer from the phase 1 config so the
    // first telegraph lands at the documented cadence.
    this._waitTimer = PHASE_CONFIG[1].wait;
  }

  /**
   * Advance the titan by one fixed tick.
   *
   * @param dt    Fixed-step delta in seconds.
   * @param _level Level geometry — unused (titan is stationary, no collision).
   * @param _nova  Nova runtime — unused (autonomous boss).
   */
  public update(dt: number, _level: LevelRuntime, _nova?: NovaRuntime): void {
    // DECISION: clear pulse at top of tick; _exitTell re-asserts it the
    // same tick it fires (single-tick pulse, same as PrismLeviathan).
    this.fireRequested = false;
    if (this.state === 'DEAD') return;

    this._updateBob(dt);

    switch (this.state) {
      case 'WAIT':
        this._waitTimer -= dt;
        if (this._waitTimer <= 0) this._enterTell();
        break;
      case 'TELL':
        this._tellTimer -= dt;
        if (this._tellTimer <= 0) this._exitTell();
        break;
      case 'RECOVER':
        this._recoverTimer -= dt;
        if (this._recoverTimer <= 0) this._enterWait();
        break;
    }
  }

  /** Phase-scaled vertical bob around `_centerY`. X stays pinned. */
  private _updateBob(dt: number): void {
    this._bobPhase += dt;
    this.x = this._centerX;
    this.y = this._centerY + Math.sin(this._bobPhase * BOB_OMEGA) * PHASE_CONFIG[this.phase].bobAmp;
  }

  private _enterWait(): void {
    this.state = 'WAIT';
    this._waitTimer = PHASE_CONFIG[this.phase].wait;
    Logger.debug('StarEaterBoss', `Phase ${this.phase} → WAIT (${this._waitTimer}s)`);
  }

  private _enterTell(): void {
    this.state = 'TELL';
    this._tellTimer = PHASE_CONFIG[this.phase].tell;
    Logger.debug('StarEaterBoss', `Phase ${this.phase} → TELL (constellation trap forming)`);
  }

  private _exitTell(): void {
    this.state = 'RECOVER';
    this._recoverTimer = PHASE_CONFIG[this.phase].recover;
    this.fireRequested = true; // single-tick pulse — trap fires
    Logger.debug('StarEaterBoss', `Phase ${this.phase} → RECOVER (window open)`);
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` outside RECOVER (titan absorbs); `'HURT'` while
   *          RECOVER; `'IGNORED'` once DEAD.
   */
  public applyShardHit(): StarEaterBossHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state !== 'RECOVER') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. Titan absorbs stomps outside RECOVER.
   * @returns `true` when the stomp connected; `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state !== 'RECOVER') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP, escalate the phase on threshold crossings, and route
   * to DEAD when the pool empties. Called only from successful
   * RECOVER-state hits.
   */
  private _takeDamage(): void {
    this.hp -= 1;
    Logger.debug('StarEaterBoss', `Hit landed — HP ${this.hp}/${this.maxHp}`);
    if (this.hp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('StarEaterBoss', 'Defeated — constellation restored');
      return;
    }
    // DECISION: phase escalates the tick the threshold is crossed, but
    // the active RECOVER window keeps its current duration — next WAIT
    // / TELL / RECOVER use the new (shorter) timings. Bob amplitude
    // takes effect immediately via _updateBob reading PHASE_CONFIG[phase].
    if (this.phase === 1 && this.hp <= PHASE_2_THRESHOLD) {
      this.phase = 2;
      Logger.debug('StarEaterBoss', 'Phase 1 → 2 (escalation)');
    } else if (this.phase === 2 && this.hp <= PHASE_3_THRESHOLD) {
      this.phase = 3;
      Logger.debug('StarEaterBoss', 'Phase 2 → 3 (final fracture)');
    }
  }
}
